import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { trapTabKey, useReturnFocusOnUnmount } from '../../../finance/dialog-focus';

/**
 * Shared look of the client «Фінанси» tab — the design-token field, label and
 * card classes and the one dialog shell both of its dialogs («Додати платіж»,
 * «Коригування») wear. Kept here so the two dialogs cannot drift apart.
 */

export const fieldClass =
  'h-10 w-full rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[14px] text-cf-ink outline-none focus:border-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink disabled:opacity-50';

export const textareaClass =
  'w-full resize-none rounded-cf-md border border-cf-line bg-cf-surface px-3 py-2 text-[14px] text-cf-ink outline-none focus:border-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink disabled:opacity-50';

export const labelClass = 'flex flex-col gap-1.5 text-[13px] text-cf-ink-muted';

export const cardClass =
  'flex flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface p-4 lg:gap-3.5 lg:px-6 lg:py-[22px]';

export const headingClass =
  'm-0 text-base leading-[21px] font-semibold tracking-[-0.02em] lg:text-[18px] lg:leading-[23px]';

/** A refusal / failure line inside a form. */
export function FormAlert({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <p
      role="alert"
      data-testid={testId}
      className="m-0 rounded-cf-md bg-cf-danger-tint px-3 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
    >
      {children}
    </p>
  );
}

/**
 * Modal shell: backdrop, `role="dialog"` labelled by its title, Escape and a
 * backdrop click close it (not while `busy`), Tab never escapes it, and focus
 * returns to the trigger on close (`features/finance/dialog-focus.ts`).
 */
export function DialogShell({
  title,
  busy,
  onClose,
  testId,
  children,
}: {
  title: string;
  busy: boolean;
  onClose: () => void;
  testId: string;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  useReturnFocusOnUnmount();

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (!busy) onClose();
      return;
    }
    trapTabKey(dialogRef.current, event);
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-cf-ink/35 p-4 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        onKeyDown={onKeyDown}
        className="my-auto flex max-h-[calc(100dvh-32px)] w-full max-w-[520px] flex-col gap-3.5 overflow-y-auto rounded-cf-lg border border-cf-line bg-cf-surface p-5 text-left leading-[normal] shadow-[0_24px_60px_rgba(0,0,0,0.16)]"
      >
        <h2 id={titleId} className="m-0 text-[18px] font-semibold tracking-[-0.02em] text-cf-ink">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
